import {NavLink} from 'react-router-dom';
import styles from './mobile_navigation.module.scss';
import menuStyles from './navigation.module.scss';
import {useAppSelector} from '../../store/store';
import {isAuthenticatedSelector} from '../../store/slices/exchange_token_slice';
import {unreadMessagesCountSelector} from '../../store/slices/chat_slice';
import {unreadNotificationsCountSelector} from '../../store/slices/notifications_slice';
import {__, transString} from '../../support/lang/translations';
import {navLinkClass} from './navigation.tsx';
import React, {useCallback, useState} from 'react';
import {classNames} from '../../support/classnames.ts';
import {useClickOutside} from '../../support/use_click_outside.ts';
import {MenuItemType} from '../../gql/pwa/graphql.ts';

type NavLinkRenderProps = {
    isActive: boolean;
    isPending: boolean;
    isTransitioning: boolean;
};

enum NavType {
    Contact = 'contact',
    Occasions = 'occasions',
    Profile = 'profile',
    Appointment = 'appointment',
    Chat = 'chat',
}

function mobileNavLinkClass({isActive, isPending}: NavLinkRenderProps) {
    return styles.mobile_menu__item + ' ' + (isActive ? styles.active : isPending ? styles.pending : '');
}

export function MobileNavigation() {
    const appConfig = useAppSelector((state) => state.appConfig.appconfig);

    const extraMenuItems = (appConfig?.menu_extra && (appConfig.menu_extra as MenuItemType[])) || [];

    const isAuthenticated = useAppSelector(isAuthenticatedSelector);
    const unreadMessagesCount = useAppSelector(unreadMessagesCountSelector);
    const unreadNotificationsCount = useAppSelector(unreadNotificationsCountSelector);

    const organisation = useAppSelector((state) => state.appConfig.organisation);
    const meData = useAppSelector((state) => state.customerInfo.me?.customer);
    const locations = useAppSelector((state) => state.appConfig.locations);

    const defaultLocationId = meData?.default_location_id;
    const defaultLocation = locations
        ? defaultLocationId
            ? locations?.filter((l) => l?.id === defaultLocationId)[0]
            : locations.length > 0
              ? locations[0]
              : null
        : null;

    let whatsAppNumber = organisation?.whatsapp_number;
    if (defaultLocation != null && defaultLocation.whatsapp_number != null) {
        whatsAppNumber = defaultLocation.whatsapp_number;
    }

    const [isOpen, setIsOpen] = useState(false);

    const ref = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLDivElement | null>(null);

    const closePopupCallback = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    useClickOutside(ref, closePopupCallback, triggerRef);

    const createNavLink = (navType: string) => {
        switch (navType) {
            case NavType.Contact:
                return (
                    <NavLink
                        to="/contact"
                        className={mobileNavLinkClass}
                        title={transString('nav.contact')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-phone"></i>
                        </span>
                    </NavLink>
                );
            case NavType.Occasions:
                return (
                    <NavLink
                        to="/occasions"
                        className={mobileNavLinkClass}
                        title={transString('nav.occasions')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-car-alt"></i>
                        </span>
                    </NavLink>
                );
            case NavType.Profile:
                return (
                    <NavLink
                        to="/profile"
                        className={mobileNavLinkClass}
                        title={transString('nav.profile')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-user"></i>
                        </span>
                    </NavLink>
                );
            case NavType.Appointment:
                return (
                    <NavLink
                        to="/appointment"
                        className={mobileNavLinkClass}
                        title={transString('nav.appointment')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-calendar-days"></i>
                        </span>
                    </NavLink>
                );
            case NavType.Chat:
                return (
                    <NavLink
                        to={whatsAppNumber ? `https://wa.me/${whatsAppNumber}` : '/chat'}
                        target={whatsAppNumber ? '_blank' : '_self'}
                        className={mobileNavLinkClass}
                        title={transString('nav.support')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-regular fa-comments"></i>
                        </span>
                        {unreadMessagesCount !== 0 ? (
                            <div className={styles.item__count}>{unreadMessagesCount}</div>
                        ) : null}
                    </NavLink>
                );
        }
    };

    return (
        <>
            <nav className={styles.nav_mobile}>
                <div className={styles.nav_mobile__background}></div>
                <div className={styles.nav_mobile__shadow}></div>
                <div
                    className={classNames(styles.nav_mobile__menu, {
                        [styles.two_columns]: !isAuthenticated,
                    })}
                >
                    <NavLink
                        to="/"
                        className={mobileNavLinkClass}
                        title={transString('nav.home')}
                        viewTransition={true}
                    >
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-grip"></i>
                        </span>
                    </NavLink>

                    {isAuthenticated && appConfig?.pwa_menu_2 && createNavLink(appConfig.pwa_menu_2)}

                    {isAuthenticated && (
                        <NavLink
                            to="/notifications"
                            className={mobileNavLinkClass}
                            title={transString('nav.inbox')}
                            viewTransition={true}
                        >
                            <span className={styles.item__icon}>
                                <i className="fa-light fa-inbox"></i>
                            </span>
                            {unreadNotificationsCount !== 0 ? (
                                <div className={styles.item__count}>{unreadNotificationsCount}</div>
                            ) : null}
                        </NavLink>
                    )}

                    {isAuthenticated && appConfig?.pwa_menu_3 && createNavLink(appConfig.pwa_menu_3)}

                    <div className={styles.mobile_menu__item} onClick={() => setIsOpen(!isOpen)} ref={triggerRef}>
                        <span className={styles.item__icon}>
                            <i className="fa-light fa-bars"></i>
                        </span>
                    </div>
                </div>
                <div
                    ref={ref}
                    className={classNames(styles.nav_mobile__overlay_menu, {
                        [styles.open]: isOpen,
                    })}
                >
                    <div className={styles.menu__close} onClick={() => setIsOpen(!isOpen)}>
                        <i className={'fa-light fa-close'} />
                    </div>

                    {isAuthenticated && appConfig?.pwa_more_chat && (
                        <NavLink
                            to={'/chat'}
                            className={navLinkClass}
                            title={transString('nav.support')}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-regular fa-comments"></i>
                            </span>
                            <span className={menuStyles.item__text}>{__('nav.support')}</span>
                            {unreadNotificationsCount !== 0 ? (
                                <div className={styles.item__count}>{unreadNotificationsCount}</div>
                            ) : null}
                        </NavLink>
                    )}

                    <div className={menuStyles.menu__header}>
                        <span>{__('nav.service_support')}</span>
                    </div>
                    {isAuthenticated && appConfig?.pwa_more_appointment && (
                        <NavLink
                            to="/appointment"
                            className={navLinkClass}
                            title={transString('nav.appointment')}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-regular fa-calendar-days"></i>
                            </span>
                            <span className={menuStyles.item__text}>{__('nav.appointment')}</span>
                        </NavLink>
                    )}
                    <NavLink
                        to="/damage"
                        className={navLinkClass}
                        title={transString('nav.damage')}
                        onClick={() => setIsOpen(false)}
                        viewTransition={true}
                    >
                        <span className={menuStyles.item__icon}>
                            <i className="fa-light fa-exclamation-triangle"></i>
                        </span>
                        <span className={menuStyles.item__text}>{__('nav.damage')}</span>
                    </NavLink>
                    <NavLink
                        to="/faults"
                        className={navLinkClass}
                        title={transString('nav.faults')}
                        onClick={() => setIsOpen(false)}
                        viewTransition={true}
                    >
                        <span className={menuStyles.item__icon}>
                            <i className="fa-regular fa-lightbulb"></i>
                        </span>
                        <span className={menuStyles.item__text}>{__('nav.faults')}</span>
                    </NavLink>
                    {appConfig?.pwa_more_contact && (
                        <NavLink
                            to="/contact"
                            className={navLinkClass}
                            title={transString('nav.contact')}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-light fa-phone"></i>
                            </span>
                            <span className={menuStyles.item__text}>{__('nav.contact')}</span>
                        </NavLink>
                    )}

                    <div className={menuStyles.menu__header}>
                        <span>{appConfig?.appname ?? ''}</span>
                    </div>
                    {appConfig?.pwa_more_occasions && (
                        <NavLink
                            to="/occasions"
                            className={navLinkClass}
                            title={transString('nav.occasions')}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-light fa-car-alt"></i>
                            </span>
                            <span className={menuStyles.item__text}>{__('nav.occasions')}</span>
                        </NavLink>
                    )}
                    <NavLink
                        to="/news"
                        className={navLinkClass}
                        title={transString('nav.news')}
                        onClick={() => setIsOpen(false)}
                        viewTransition={true}
                    >
                        <span className={menuStyles.item__icon}>
                            <i className="fa-regular fa-newspaper"></i>
                        </span>
                        <span className={menuStyles.item__text}>{__('nav.news')}</span>
                    </NavLink>
                    {appConfig?.pwa_more_reviews && (
                        <NavLink
                            to="/reviews"
                            className={navLinkClass}
                            title={transString('nav.reviews')}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-regular fa-star"></i>
                            </span>
                            <span className={menuStyles.item__text}>{__('nav.reviews')}</span>
                        </NavLink>
                    )}

                    {extraMenuItems != null && extraMenuItems.length > 0 && (
                        <div className={menuStyles.menu__header}>
                            <span>{__('nav.information')}</span>
                        </div>
                    )}
                    {extraMenuItems.map((extraMenuItem, i) => (
                        <NavLink
                            key={'extra-menu-item-' + i}
                            to={extraMenuItem.url || ''}
                            className={navLinkClass}
                            onClick={() => setIsOpen(false)}
                            viewTransition={true}
                        >
                            <span className={menuStyles.item__icon}>
                                <i className="fa-light fa-link"></i>
                            </span>
                            <span className={menuStyles.item__text}>{extraMenuItem.name}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
        </>
    );
}
